import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Chip,
  Box,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useMutation } from "@apollo/client";
import { LIST_EMPLOYEES } from "../../graphql/operations";
import { ASSIGN_FORM_TO_EMPLOYEES } from "../../graphql/operations";

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// interface EmployeeAssignment {
//   employeeId: string;
//   employeeName: string;
// }

interface AssignFormDialogProps {
  open: boolean;
  onClose: () => void;
  formId: string;
  formTitle: string;
}

const AssignFormDialog: React.FC<AssignFormDialogProps> = ({
  open,
  onClose,
  formId,
  formTitle,
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [message, setMessage] = useState("");

  const { data: employeesData } = useQuery(LIST_EMPLOYEES);
  const [assignForm, { loading, error }] = useMutation(
    ASSIGN_FORM_TO_EMPLOYEES
  );

  const employees = employeesData?.listEmployees?.items || [];

  const handleSubmit = async () => {
    if (selectedEmployees.length === 0) return;

    try {
      // Create employee assignments with both ID and name
      const employeeAssignments = selectedEmployees.map((emp) => ({
        employeeId: emp.id,
        employeeName: `${emp.firstName} ${emp.lastName}`
      }));

      await assignForm({
        variables: {
          formId,
          formName: formTitle, // Use the formTitle prop as formName
          employeeAssignments, // Send array of objects with id and name
          dueDate: dueDate?.toISOString(),
          message: message || undefined, // Optional message
        },
      });

      // Reset form
      setSelectedEmployees([]);
      setDueDate(null);
      setMessage("");
      onClose();
    } catch (err) {
      console.error("Error assigning form:", err);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setSelectedEmployees([]);
    setDueDate(null);
    setMessage("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Form to Employees</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Form: <strong>{formTitle}</strong>
          </Typography>

          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName} (${option.email})`
              }
              value={selectedEmployees}
              onChange={(_, newValue) => setSelectedEmployees(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employees"
                  placeholder="Choose employees..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.firstName} ${option.lastName}`}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </FormControl>

          <DatePicker
            label="Due Date"
            value={dueDate}
            onChange={(newValue) => setDueDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal",
              },
            }}
          />

          <TextField
            label="Custom Message (Optional)"
            multiline
            rows={3}
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            margin="normal"
            placeholder="Add a custom message for the employees..."
          />

          {/* Preview of what will be sent */}
          {selectedEmployees.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Assignment Preview:
              </Typography>
              <Typography variant="body2">
                <strong>Form:</strong> {formTitle}
              </Typography>
              <Typography variant="body2">
                <strong>Employees:</strong> {selectedEmployees.length} selected
              </Typography>
              <Typography variant="body2">
                <strong>Due Date:</strong> {dueDate ? dueDate.toLocaleDateString() : 'Not set'}
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            Error: {error.message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || selectedEmployees.length === 0}
        >
          {loading
            ? "Assigning..."
            : `Assign to ${selectedEmployees.length} Employee(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFormDialog;